/**
 * Ferramentas de anotação manual da mesa (E36.F3): estado de UI + toolbar. // ux-neutrality:allow doc-comment
 *
 * Um modo ativo por vez: ativar uma ferramenta de desenho desliga a outra; o // ux-neutrality:allow doc-comment
 * pai (ContestedCanvasTools) coordena com régua e pan. Delete/Backspace apaga // ux-neutrality:allow doc-comment
 * a anotação selecionada (ignorando teclas vindas de campos de texto — mesmo // ux-neutrality:allow doc-comment
 * guard do OverlapViewport); Esc limpa a seleção. // ux-neutrality:allow doc-comment
 */
import { useEffect, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import {
  ANNOTATION_COLOR_HEX,
  type AnnotationColor,
  type AnnotationTool,
} from '@/lib/mesa_annotations';

export interface AnnotationUiState {
  drawTool: AnnotationTool | null;
  drawColor: AnnotationColor;
  selectedId: string | null;
  setTool: (tool: AnnotationTool | null) => void;
  setColor: (color: AnnotationColor) => void;
  select: (id: string | null) => void;
  deleteSelected: () => void;
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.isContentEditable
  );
}

export function useAnnotationUi(removeAnnotation: (id: string) => void): AnnotationUiState {
  const [drawTool, setDrawTool] = useState<AnnotationTool | null>(null);
  const [drawColor, setDrawColor] = useState<AnnotationColor>('red');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Delete/Backspace apaga a seleção; Esc limpa. Guard: teclas digitadas em
  // campos de texto (chat, formulários) nunca apagam anotação.
  useEffect(() => {
    if (selectedId === null) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (isTextEntry(event.target)) return;
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        setSelectedId((current) => {
          if (current !== null) removeAnnotation(current);
          return null;
        });
      }
      if (event.key === 'Escape') setSelectedId(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedId, removeAnnotation]);

  return {
    drawTool,
    drawColor,
    selectedId,
    // Reativar a ferramenta corrente desliga (toggle); trocar de ferramenta
    // limpa a seleção (o gesto seguinte é de desenho).
    setTool: (tool) => {
      setDrawTool((current) => (current === tool ? null : tool));
      setSelectedId(null);
    },
    setColor: setDrawColor,
    select: setSelectedId,
    deleteSelected: () => {
      setSelectedId((current) => {
        if (current !== null) removeAnnotation(current);
        return null;
      });
    },
  };
}

const COLOR_LABEL: Record<AnnotationColor, string> = {
  green: ptBR.workspaceCaso.annotateColorGreen,
  amber: ptBR.workspaceCaso.annotateColorAmber,
  red: ptBR.workspaceCaso.annotateColorRed,
};

export interface AnnotationToolbarProps {
  ui: AnnotationUiState;
  saveError: boolean;
}

export function AnnotationToolbar({ ui, saveError }: AnnotationToolbarProps): JSX.Element {
  return (
    <>
      <span className="inspection-divider" aria-hidden="true" />
      <button
        type="button"
        data-testid="annotate-arrow"
        aria-pressed={ui.drawTool === 'arrow'}
        title={ptBR.workspaceCaso.annotateGestureTitle}
        onClick={() => ui.setTool('arrow')}
      >
        {ptBR.workspaceCaso.annotateArrow}
      </button>
      <button
        type="button"
        data-testid="annotate-circle"
        aria-pressed={ui.drawTool === 'circle'}
        title={ptBR.workspaceCaso.annotateGestureTitle}
        onClick={() => ui.setTool('circle')}
      >
        {ptBR.workspaceCaso.annotateCircle}
      </button>
      {(Object.keys(ANNOTATION_COLOR_HEX) as AnnotationColor[]).map((color) => (
        <button
          key={color}
          type="button"
          className="annotation-color"
          data-testid={`annotate-color-${color}`}
          aria-pressed={ui.drawColor === color}
          aria-label={COLOR_LABEL[color]}
          title={COLOR_LABEL[color]}
          style={{ '--annotation-color': ANNOTATION_COLOR_HEX[color] } as React.CSSProperties}
          onClick={() => ui.setColor(color)}
        />
      ))}
      <button
        type="button"
        data-testid="annotate-delete"
        disabled={ui.selectedId === null}
        onClick={() => ui.deleteSelected()}
      >
        {ptBR.workspaceCaso.annotateDelete}
      </button>
      {saveError && (
        <span role="alert" className="annotation-save-error" data-testid="annotate-save-error">
          {ptBR.workspaceCaso.annotateSaveError}
        </span>
      )}
    </>
  );
}
